class StyleManager {
  constructor(options = {}) {
    this.injectedStyles = new Set()

    // Bind methods
    this.initialize = this.initialize.bind(this)
    this.injectStylesFromHTML = this.injectStylesFromHTML.bind(this)
    this.scopeCSS = this.scopeCSS.bind(this)
    this.removeAllInjectedStyles = this.removeAllInjectedStyles.bind(this)
    this.addCustomStyles = this.addCustomStyles.bind(this)
    this.updateStylesFromDocument = this.updateStylesFromDocument.bind(this)
  }

  initialize() {
    // Style manager doesn't need specific initialization
  }

  injectStylesFromHTML(htmlContent, scopeSelector = ".editor") {
    if (!htmlContent) return

    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, "text/html")

    // Extract and inject styles into the editor
    const styleElements = doc.querySelectorAll("style")

    if (styleElements.length > 0) {
      // Remove existing injected styles
      this.removeAllInjectedStyles()

      // Inject new styles with scoping to the editor
      styleElements.forEach((styleEl, index) => {
        const newStyle = document.createElement("style")
        newStyle.setAttribute("data-html-editor", "true")
        newStyle.setAttribute("data-style-index", index.toString())

        // Scope styles to the editor container
        const scopedCSS = this.scopeCSS(styleEl.textContent, scopeSelector)
        newStyle.textContent = scopedCSS
        document.head.appendChild(newStyle)

        // Track the injected style
        this.injectedStyles.add(newStyle)
      })
    }
  }

  scopeCSS(cssContent, scopeSelector = ".editor") {
    if (!cssContent) return ""

    return cssContent.replace(
      /([^{}]+)\s*\{/g,
      (match, selector) => {
        const trimmedSelector = selector.trim()

        // Don't scope body or html selectors, just replace them
        if (trimmedSelector === "body" || trimmedSelector === "html") {
          return `${scopeSelector} {`
        }

        // Don't scope selectors that already include the scope
        if (trimmedSelector.includes(scopeSelector)) {
          return match
        }

        // For @media queries and other at-rules, don't scope
        if (trimmedSelector.startsWith("@")) {
          return match
        }

        // Handle multiple selectors (comma-separated)
        const selectors = trimmedSelector.split(',').map(sel => {
          const cleanSel = sel.trim()

          // Handle pseudo-class selectors like .class:hover, .class:focus, etc.
          if (cleanSel.includes(':')) {
            // Split on the first colon to separate base selector from pseudo-class
            const colonIndex = cleanSel.indexOf(':')
            const baseSelector = cleanSel.substring(0, colonIndex)
            const pseudoClass = cleanSel.substring(colonIndex)

            // Use proper scoping for pseudo-class selectors
            const scopedSelector = `${scopeSelector} ${baseSelector}${pseudoClass}`
            return scopedSelector
          }

          // Regular selector scoping
          const scopedSelector = `${scopeSelector} ${cleanSel}`
          return scopedSelector
        })

        return `${selectors.join(', ')} {`
      }
    )
  }

  removeAllInjectedStyles() {
    // Remove all styles marked as injected by this manager
    const existingStyles = document.querySelectorAll("style[data-html-editor]")
    existingStyles.forEach((style) => {
      style.remove()
      this.injectedStyles.delete(style)
    })
  }

  addCustomStyles(cssContent, id = null, scopeSelector = ".editor") {
    const styleElement = document.createElement("style")
    styleElement.setAttribute("data-html-editor", "true")

    if (id) {
      styleElement.setAttribute("data-style-id", id)
    }

    // Scope the CSS if needed
    const scopedCSS = scopeSelector ? this.scopeCSS(cssContent, scopeSelector) : cssContent
    styleElement.textContent = scopedCSS

    document.head.appendChild(styleElement)
    this.injectedStyles.add(styleElement)

    return styleElement
  }

  removeStyleById(id) {
    const styleElement = document.querySelector(`style[data-html-editor][data-style-id="${id}"]`)
    if (styleElement) {
      styleElement.remove()
      this.injectedStyles.delete(styleElement)
      return true
    }
    return false
  }

  updateStylesFromDocument(htmlDocument, scopeSelector = ".editor") {
    // Extract styles from a complete HTML document and inject them
    if (!htmlDocument) return

    try {
      const parser = new DOMParser()
      const doc = parser.parseFromString(htmlDocument, "text/html")

      // Get all style elements from the document
      const styleElements = doc.querySelectorAll("style")

      if (styleElements.length > 0) {
        // Remove existing styles first
        this.removeAllInjectedStyles()

        // Add each style element
        styleElements.forEach((styleEl, index) => {
          this.addCustomStyles(
            styleEl.textContent,
            `document-${index}`,
            scopeSelector
          )
        })
      }
    } catch (error) {
      console.error("Error updating styles from document:", error)
    }
  }

  getAllInjectedStyles() {
    return Array.from(this.injectedStyles).map(style => ({
      id: style.getAttribute("data-style-id"),
      index: style.getAttribute("data-style-index"),
      content: style.textContent
    }))
  }

  hasInjectedStyles() {
    return this.injectedStyles.size > 0
  }

  // Method to create a scoped version of CSS for a specific context
  createScopedStylesheet(cssContent, scopeSelector, id = null) {
    const scopedCSS = this.scopeCSS(cssContent, scopeSelector)
    return this.addCustomStyles(scopedCSS, id, null) // null because already scoped
  }

  // Method to temporarily disable all injected styles
  disableAllStyles() {
    this.injectedStyles.forEach(style => {
      style.disabled = true
    })
  }

  // Method to re-enable all injected styles
  enableAllStyles() {
    this.injectedStyles.forEach(style => {
      style.disabled = false
    })
  }

  // Method to get CSS rules that match a specific selector
  getMatchingRules(selector) {
    const matchingRules = []

    this.injectedStyles.forEach(styleElement => {
      if (styleElement.sheet) {
        try {
          const rules = styleElement.sheet.cssRules || styleElement.sheet.rules
          for (let i = 0; i < rules.length; i++) {
            const rule = rules[i]
            if (rule.selectorText && rule.selectorText.includes(selector)) {
              matchingRules.push({
                selector: rule.selectorText,
                style: rule.style.cssText,
                styleSheet: styleElement
              })
            }
          }
        } catch (e) {
          // Some stylesheets might not be accessible due to CORS
          console.warn("Could not access stylesheet rules:", e)
        }
      }
    })

    return matchingRules
  }

  destroy() {
    // Clean up all injected styles when the editor is destroyed
    this.removeAllInjectedStyles()
    this.injectedStyles.clear()
  }
}

// Export as a plugin-compatible object
export default {
  create: (options) => new StyleManager(options),
  StyleManager,
}